import { memo, useState } from 'react';
import { cn } from '../../utils/cn';

interface PartnerAvatarProps {
  /** The Discord avatar the bot last saw, if any. */
  avatarUrl: string | null | undefined;
  name: string;
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE = {
  sm: 'w-7 h-7 text-[11px]',
  md: 'w-8 h-8 text-xs',
} as const;

/**
 * A partner team member's Discord avatar, or an initial tile like
 * `StaffAvatar`'s when there is none (or it fails to load). Partners are not
 * necessarily players, so there is no Minecraft head to fall back on.
 * Decorative: always pair it with the name.
 */
export const PartnerAvatar = memo(({ avatarUrl, name, size = 'sm', className }: PartnerAvatarProps) => {
  const [failed, setFailed] = useState(false);
  const initial = (name.trim().replace(/^@/, '')[0] || '?').toUpperCase();
  if (avatarUrl && !failed && /^https:\/\//.test(avatarUrl)) {
    return (
      <img
        src={avatarUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={cn('flex-shrink-0 rounded-md object-cover bg-glass-100/10 ring-1 ring-glass-200/15', SIZE[size], className)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex-shrink-0 inline-flex items-center justify-center rounded-md font-semibold text-accent-200 bg-accent-500/15 ring-1 ring-glass-200/15',
        SIZE[size],
        className,
      )}
    >
      {initial}
    </span>
  );
});
PartnerAvatar.displayName = 'PartnerAvatar';
